import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";
import {SearchDiagnosticsService} from "../../service/SearchDiagnosticsService";
import {Loading} from "../../utils/InlineComponents";

const service = new SearchDiagnosticsService();

// `status` is stamped by the scraper, the only place its precedence lives; this map only
// colours it. An unknown value renders neutral.
export const SITE_STATUS_SEVERITY = {
    blocked: 'danger',
    errored: 'danger',
    zero_results: 'warning',
    suggested: 'success',
    nothing_new: 'info',
};

const SITE_OUTCOME_SEVERITY = {
    suggested: 'success',
    already_known: 'info',
    duplicate: 'info',
    spider_error: 'danger',
};

// Actionable first: broken, then empty, then the rest.
const RANK = {danger: 0, warning: 1, info: 2, success: 3};
const rankOf = (severity) => RANK[severity] === undefined ? RANK.info : RANK[severity];

const ProductSiteSearchDiagnostics = ({selectedProduct}) => {

    const {t, setError} = React.useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);

    useEffect(() => {
        if (!selectedProduct?.id) return;
        setLoading(true);
        service.getProductSiteSearchDiagnostics(selectedProduct.id)
            .then(result => { setRows(result || []); setLoading(false); })
            .catch(error => { setError(error); setError(null); setLoading(false); });
    }, [selectedProduct?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const label = (key, options) => t(`search_diagnostics.${key}`, options);

    if (loading) return <Loading/>;
    if (!rows.length) return <Message severity="info" text={label('site_never_searched')}/>;

    const emptyMessage = t('actions.empty_message');
    const ordered = [...rows].sort((a, b) =>
        rankOf(SITE_STATUS_SEVERITY[a.status]) - rankOf(SITE_STATUS_SEVERITY[b.status]));
    const lastPage = (row) => (row.pages || [])[(row.pages || []).length - 1] || {};
    const openLink = (url) => url
        ? <a href={url} target="_blank" rel="noreferrer">{label('open')}</a> : '-';

    const hostBody = (row) => <>
        {row.host}
        {row.target === 'supplier' &&
            <Tag severity="info" value={label('site_supplier')} style={{marginLeft: '.4em'}}/>}
    </>;
    const whenBody = (row) => <>
        {row.searched_at && new Date(row.searched_at).toLocaleString()}
        {row.run ? <> · {label(`trigger_${row.run.trigger}`)}</> : <> · {label('run_expired')}</>}
    </>;
    const keywordBody = (row) => (row.queries || []).map((query, index) =>
        <div key={index}><code style={{userSelect: 'all'}}>{query.query || '-'}</code></div>);
    const httpBody = (row) => lastPage(row).http_status == null
        ? label('site_no_response') : lastPage(row).http_status;
    const statusBody = (row) => <Tag severity={SITE_STATUS_SEVERITY[row.status] || null}
                                     value={label(`site_status_${row.status}`)}/>;

    // Host parsers return at most ~10 results per page, so the expansion shows them all
    // without a filter.
    const expansion = (row) => <div style={{padding: '.5em 1em'}}>
        <h6>{label('site_pages')}</h6>
        <DataTable value={row.pages || []} className="p-datatable-sm" emptyMessage={emptyMessage}>
            <Column header={label('site_keyword')}
                    body={(page) => ((row.queries || [])[page.query_index] || {}).query || '-'}/>
            <Column header={label('http')} style={{width: '6rem'}}
                    body={(page) => page.http_status == null ? label('site_no_response') : page.http_status}/>
            <Column header={label('results')} style={{width: '6rem'}}
                    body={(page) => page.result_count == null ? '-' : page.result_count}/>
            <Column header={label('site_status_blocked')} style={{width: '7rem'}}
                    body={(page) => page.blocked ? '✓' : ''}/>
            <Column header={label('reason')} body={(page) => page.error || ''}/>
            <Column header={label('engine_url')} style={{width: '5rem'}}
                    body={(page) => openLink(page.url)}/>
        </DataTable>
        <h6 style={{marginTop: '1em'}}>{label('results')}</h6>
        <DataTable value={[...(row.results || [])].sort((a, b) =>
                       rankOf(SITE_OUTCOME_SEVERITY[a.outcome]) - rankOf(SITE_OUTCOME_SEVERITY[b.outcome]))}
                   className="p-datatable-sm" emptyMessage={emptyMessage}>
            <Column header={label('url')} body={(result) => result.url
                ? <a href={result.url} target="_blank" rel="noreferrer">{result.name || result.url}</a>
                : (result.name || '-')}/>
            <Column header={label('site_price')} style={{width: '9rem'}}
                    body={(result) => result.price == null ? '-' : `${result.price} ${result.currency || ''}`}/>
            <Column header={label('outcome')}
                    body={(result) => <Tag severity={SITE_OUTCOME_SEVERITY[result.outcome] || null}
                                           value={label(`site_outcome_${result.outcome}`)}/>}/>
            <Column header={label('reason')} body={(result) => result.detail || ''}/>
        </DataTable>
    </div>;

    return <DataTable value={ordered} dataKey="_id" expandedRows={expandedRows}
                      onRowToggle={(e) => setExpandedRows(e.data)} rowExpansionTemplate={expansion}
                      className="p-datatable-sm" emptyMessage={emptyMessage}>
        <Column expander style={{width: '3em'}}/>
        <Column header={label('host')} body={hostBody}/>
        <Column header={label('site_last_search')} body={whenBody}/>
        <Column header={label('site_keyword')} body={keywordBody}/>
        <Column header={label('http')} body={httpBody} style={{width: '6rem'}}/>
        <Column header={label('site_results_suggested')} style={{width: '9rem'}}
                body={(row) => `${row.counts?.returned ?? 0} / ${row.counts?.suggested ?? 0}`}/>
        <Column header={label('site_status')} body={statusBody}/>
        <Column header={label('engine_url')} style={{width: '5rem'}}
                body={(row) => openLink((row.queries || [])[0]?.url)}/>
    </DataTable>;
};

export default ProductSiteSearchDiagnostics;
